import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// shadcn-style Alert. Pattern:
//   <Alert variant="error">
//     <AlertTitle>Something went wrong</AlertTitle>
//     <AlertDescription>{message}</AlertDescription>
//   </Alert>

const alertVariants = cva(
  'relative w-full rounded-xl border p-3 text-sm [&>svg]:absolute [&>svg]:left-3 [&>svg]:top-3 [&>svg+*]:pl-6',
  {
    variants: {
      variant: {
        default: 'border-border/60 bg-card text-foreground',
        info: 'border-blue-300/40 bg-blue-50/50 dark:border-blue-400/20 dark:bg-blue-500/10 text-blue-900 dark:text-blue-100',
        success: 'border-emerald-300/40 bg-emerald-50/50 dark:border-emerald-400/20 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
        warning: 'border-amber-300/40 bg-amber-50/50 dark:border-amber-400/20 dark:bg-amber-500/10 text-amber-900 dark:text-amber-100',
        error: 'border-rose-300/40 bg-rose-50/50 dark:border-rose-400/20 dark:bg-rose-500/10 text-rose-900 dark:text-rose-100',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h5 ref={ref} className={cn('mb-0.5 font-semibold leading-tight', className)} {...props}>
    {children}
  </h5>
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm leading-snug', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
